import type { AssetRef, AudienceI18nSnapshot, ImageAdjust, LegacyAudienceConfig, ServiceAction, Transport } from '@/domain/audience/types'
import type { AudienceCapabilities, AudienceI18nV1, AudienceReleaseContentV1, PublishedAssetRef, ResolvedAudienceSkinV1 } from '@/domain/release/types'
import { sha256Hex } from '@/core/release/verifiedFetch'
import { assertSafeArtifactUrl } from '@/core/release/verifiedFetch'

type JsonRecord = Record<string, unknown>

export interface ReleaseAdaptOptions { skin?: ResolvedAudienceSkinV1; capabilities?: AudienceCapabilities }

export async function adaptReleaseContent(content: AudienceReleaseContentV1, assets: Record<string, unknown> | null, i18n: AudienceI18nV1 | null, options: ReleaseAdaptOptions = {}): Promise<LegacyAudienceConfig> {
  const home = record(content.home)
  const mode = record(content.mode)
  const overview = isRecord(content.overview) ? content.overview : null
  const notice = isRecord(content.entryNotice) ? content.entryNotice : null
  const transports = items(record(content.transports)).map((value) => adaptTransport(value, assets))
  const zonesContainer = record(content.zones)
  const servicesContainer = record(content.services)
  const routes = new Map(items(record(content.routes)).map((route) => [text(route.id), route]))
  for (const transport of transports) {
    const source = items(record(content.transports)).find((item) => text(item.id) === transport.id)
    if (!source) continue
    const entryRoute = routes.get(text(record(source.entry).generalRouteId))
    const exitRoute = routes.get(text(record(source.exit).generalRouteId))
    if (entryRoute && !transport.entry?.overviewImage) transport.entry = { ...transport.entry, overviewImage: asset(assets, text(entryRoute.assetId)) }
    if (exitRoute && !transport.exit?.routeImage) transport.exit = { ...transport.exit, routeImage: asset(assets, text(exitRoute.assetId)) }
  }
  const config: LegacyAudienceConfig = {
    projectName: text(content.projectName),
    settings: { versionMode: record(content.settings).versionMode === 'international' ? 'international' : 'zh' },
    home: {
      venue: text(home.venueLabel), showVenue: boolean(home.showVenue), title: text(home.headline), subtitle: text(home.subtitle),
      image: publishedAsset(options.skin?.assets.hero) || asset(assets, text(home.heroAssetId)), logo: publishedAsset(options.skin?.assets.logo), imageAdjust: adjust(home.heroImageAdjust),
      buttonIcon: text(record(home.primaryAction).icon), buttonTitle: text(record(home.primaryAction).title), buttonText: text(record(home.primaryAction).description),
    },
    mode: {
      entryIcon: text(record(mode.entryAction).icon), entryTitle: text(record(mode.entryAction).title), entryText: text(record(mode.entryAction).description),
      exitIcon: text(record(mode.exitAction).icon), exitTitle: text(record(mode.exitAction).title), exitText: text(record(mode.exitAction).description),
      serviceIcon: text(record(mode.serviceAction).icon), serviceTitle: text(record(mode.serviceAction).title), serviceText: text(record(mode.serviceAction).description),
      vrIcon: text(record(mode.vrAction).icon), vrTitle: text(record(mode.vrAction).title), vrText: text(record(mode.vrAction).description),
      vrScenes: options.capabilities?.panorama === false ? [] : adaptVrScenes(content.vrZone, assets),
    },
    overview: overview ? {
      entryImage: asset(assets, text(overview.entryAssetId)), exitImage: asset(assets, text(overview.exitAssetId)),
      entryImageAdjust: adjust(overview.entryImageAdjust), exitImageAdjust: adjust(overview.exitImageAdjust),
      entryNote: text(overview.entryNote), exitNote: text(overview.exitNote),
    } : undefined,
    entryNotice: notice ? { enabled: true, menuTitle: text(notice.menuTitle), menuText: text(notice.menuText), contentHtml: text(notice.contentHtml) } : undefined,
    lostFound: isRecord(content.lostFound) ? { ...content.lostFound, enabled: boolean(content.lostFound.enabled) } : undefined,
    transports: { items: transports },
    zones: {
      title: text(zonesContainer.title), note: text(zonesContainer.note), overviewImage: asset(assets, text(zonesContainer.overviewAssetId)),
      overviewImageAdjust: adjust(zonesContainer.overviewImageAdjust),
      items: items(zonesContainer).map((zone) => ({
        id: text(zone.id), name: text(zone.name), color: text(zone.color), gate: text(zone.gateLabel), distance: text(zone.distance), note: text(zone.note),
        steps: strings(zone.steps), image: asset(assets, text(zone.assetId) || text(routes.get(text(zone.routeId))?.assetId)), imageAdjust: adjust(zone.imageAdjust),
        entryBindings: [{ transportId: text(zone.transportId), ...(text(zone.gateId) ? { gateId: text(zone.gateId) } : {}) }],
      })),
    },
    vrZone: options.capabilities?.panorama === false ? undefined : adaptVr(content.vrZone, assets),
    summary: isRecord(content.summary) ? mapStringRecord(content.summary) : undefined,
    services: {
      title: text(servicesContainer.title), note: text(servicesContainer.note),
      items: items(servicesContainer).map((service) => ({
        id: text(service.id), title: text(service.name), detailTitle: text(service.detailTitle), detailNote: text(service.detailNote),
        text: text(service.summary), detailText: text(service.descriptionHtml), image: asset(assets, text(service.assetId)), imageAdjust: adjust(service.imageAdjust),
        enabled: service.enabled !== false, promotion: boolean(service.promotion), icon: text(service.icon), phone: telephone(service.phone), actions: adaptServiceActions(service.actions),
      })),
      orderPromotion: adaptPromotion(record(servicesContainer.orderPromotionDetail), assets),
    },
    imageAnnotations: adaptAnnotations(content.imageAnnotations, assets),
  }
  config.i18n = await adaptI18n(i18n, content)
  return config
}

function adaptServiceActions(value: unknown): ServiceAction[] {
  return array(value).filter(isRecord).flatMap((action): ServiceAction[] => {
    const label = text(action.label)
    if (action.type === 'phone') { const phone = telephone(action.phone); return phone ? [{ type: 'phone', phone, ...(label ? { label } : {}) }] : [] }
    if (action.type === 'map') {
      const longitude = number(action.longitude, Number.NaN); const latitude = number(action.latitude, Number.NaN)
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return []
      return [{ type: 'map', name: text(action.name), longitude, latitude, travelMode: travelMode(action.travelMode), ...(label ? { label } : {}) }]
    }
    if (action.type === 'order' || action.type === 'external') { const url = text(action.url); return url ? [{ type: action.type, url, ...(label ? { label } : {}) }] : [] }
    return []
  })
}

function adaptTransport(value: JsonRecord, assets: Record<string, unknown> | null): Transport {
  const entry = record(value.entry)
  const exit = record(value.exit)
  return {
    id: text(value.id), name: text(value.name), enabled: value.enabled !== false, icon: text(value.icon), color: text(value.color),
    entry: {
      title: text(entry.title), summary: text(entry.summary), note: text(entry.note), originTitle: text(entry.originTitle),
      distance: text(entry.distance), duration: text(entry.duration), steps: strings(entry.steps),
      overviewImage: asset(assets, text(entry.overviewAssetId)), overviewImageAdjust: adjust(entry.overviewImageAdjust),
      gates: array(entry.gates).filter(isRecord).map((gate) => ({ id: text(gate.id), name: text(gate.name), image: asset(assets, text(gate.assetId)), imageAdjust: adjust(gate.imageAdjust) })),
      origins: array(entry.origins).filter(isRecord).map((origin) => ({
        id: text(origin.id), name: text(origin.name), enabled: origin.enabled !== false, order: number(origin.order),
      })),
      routes: array(entry.routes).filter(isRecord).map((route) => ({
        id: text(route.id), originId: text(route.originId), gateId: text(route.gateId),
        image: asset(assets, text(route.assetId)), imageAdjust: adjust(route.imageAdjust), distance: text(route.distance),
        duration: text(route.duration), note: text(route.note), steps: strings(route.steps),
      })),
    },
    exit: {
      title: text(exit.title), summary: text(exit.summary), note: text(exit.note), distance: text(exit.distance), duration: text(exit.duration), steps: strings(exit.steps),
      routeImage: asset(assets, text(exit.routeAssetId)), routeImageAdjust: adjust(exit.routeImageAdjust),
      locations: array(exit.locations).filter(isRecord).map((location) => ({
        id: text(location.id), name: text(location.name), address: text(location.address), enabled: location.enabled !== false,
        image: asset(assets, text(location.assetId)), imageAdjust: adjust(location.imageAdjust), navigationEnabled: boolean(location.navigationEnabled),
        longitude: number(location.longitude), latitude: number(location.latitude), coordType: coordinateType(location.coordType), travelMode: travelMode(location.travelMode),
      })),
    },
  }
}

function adaptVr(value: unknown, assets: Record<string, unknown> | null): LegacyAudienceConfig['vrZone'] {
  if (!isRecord(value)) return undefined
  const panoramas = panoramaAssets(assets)
  const preferred = panoramas.find((item) => text(item.sourcePath).includes('vrZone')) || panoramas[0]
  return {
    enabled: boolean(value.enabled) && Boolean(preferred), icon: text(value.icon), title: text(value.title), text: text(value.text),
    initialView: initialView(value.initialView), panorama: preferred ? panoramaResource(preferred) : undefined,
  }
}

function adaptVrScenes(value: unknown, assets: Record<string, unknown> | null): NonNullable<LegacyAudienceConfig['mode']>['vrScenes'] {
  if (!isRecord(value) || value.enabled !== true) return []
  const sceneIds = array(value.sceneIds).map(text).filter(Boolean)
  return panoramaAssets(assets).filter((item) => !text(item.sourcePath).includes('vrZone')).map((item, index) => ({
    id: sceneIds[index] || text(item.assetId), name: text(item.name) || `VR 场景 ${index + 1}`, enabled: true,
    initialView: initialView(value.initialView), panorama: panoramaResource(item),
  }))
}

function panoramaAssets(assets: Record<string, unknown> | null): JsonRecord[] {
  return assets && Array.isArray(assets.items) ? assets.items.filter(isRecord).filter((item) => {
    if (item.kind !== 'panorama-manifest' || typeof item.url !== 'string') return false
    try { assertSafeArtifactUrl(item.url); return true } catch { return false }
  }) : []
}
function panoramaResource(item: JsonRecord) {
  return { resourceType: 'pannellum-multires' as const, processStatus: 'ready', manifestUrl: text(item.url), manifestSha256: text(item.sha256), name: text(item.name) }
}
function initialView(value: unknown) { const source = record(value); return { yaw: number(source.yaw), pitch: number(source.pitch, -1), hfov: number(source.hfov, 82) } }

function adaptPromotion(value: JsonRecord, assets: Record<string, unknown> | null): NonNullable<LegacyAudienceConfig['services']>['orderPromotion'] {
  if (!Object.keys(value).length) return undefined
  return {
    enabled: boolean(value.enabled), title: text(value.title), summary: text(value.summary), menuImage: asset(assets, text(value.menuAssetId)),
    menuImageAdjust: adjust(value.menuImageAdjust), orderUrl: text(value.orderUrl), orderQrImage: asset(assets, text(value.orderQrAssetId)),
    orderQrImageAdjust: adjust(value.orderQrImageAdjust), orderButtonText: text(value.orderButtonText), orderNotice: text(value.orderNotice),
  }
}

function adaptAnnotations(value: unknown, assets: Record<string, unknown> | null): LegacyAudienceConfig['imageAnnotations'] {
  const result: NonNullable<LegacyAudienceConfig['imageAnnotations']> = {}
  for (const annotation of array(value).filter(isRecord)) {
    const referenceKey = text(annotation.referenceKey)
    if (!referenceKey) continue
    result[referenceKey] = {
      moduleKey: referenceKey, baseImage: asset(assets, text(annotation.assetId)),
      markers: array(annotation.markers).filter(isRecord).map((marker) => ({
        id: text(marker.markerKey), markerKey: text(marker.markerKey), name: text(marker.title), content: text(marker.body),
        x: number(marker.x), y: number(marker.y), enabled: true,
      })),
    }
  }
  return result
}

async function adaptI18n(value: AudienceI18nV1 | null, content: AudienceReleaseContentV1): Promise<AudienceI18nSnapshot | undefined> {
  if (!value) return undefined
  const entries: NonNullable<AudienceI18nSnapshot['entries']> = {}
  for (const item of value.content) {
    const source = canonicalSource(content, item.path, item.itemId)
    if (!source || (await sha256Hex(source)).toLowerCase() !== item.sourceHash.toLowerCase()) continue
    entries[`${item.path}::${item.itemId || ''}`] = { source, sourceHash: item.sourceHash, path: item.path, itemId: item.itemId, target: item.value }
  }
  return { schemaVersion: value.schemaVersion, entries, en: value.ui }
}

function canonicalSource(content: AudienceReleaseContentV1, path: string, itemId: string | null): string {
  const tokens = path.replace(/\[\{id\}\]/g, itemId ? `[${itemId}]` : '[]').split('.').filter(Boolean)
  let current: unknown = content
  for (const token of tokens) {
    const match = token.match(/^([^[]+)(?:\[([^\]]+)\])?$/)
    if (!match || !isRecord(current)) return ''
    current = current[match[1] || '']
    const selector = match[2]
    if (selector !== undefined) {
      if (!Array.isArray(current)) return ''
      current = current.find((candidate) => isRecord(candidate) && String(candidate.id || '') === selector)
    }
  }
  return typeof current === 'string' ? current : ''
}

function asset(assets: Record<string, unknown> | null, id: string): AssetRef | undefined {
  if (!id || !assets || !Array.isArray(assets.items)) return undefined
  const item = assets.items.find((candidate) => isRecord(candidate) && candidate.assetId === id)
  if (!isRecord(item) || typeof item.url !== 'string') return undefined
  try {
    const url = assertSafeArtifactUrl(item.url).href
    return { assetId: id, originalUrl: url, hdUrl: url, width: number(item.width), height: number(item.height) }
  } catch { return undefined }
}

function publishedAsset(value: PublishedAssetRef | null | undefined): AssetRef | undefined {
  if (!value) return undefined
  try { const url = assertSafeArtifactUrl(value.url).href; return { assetId: value.assetId, originalUrl: url, hdUrl: url, width: value.width, height: value.height } } catch { return undefined }
}

function adjust(value: unknown): ImageAdjust | undefined {
  if (!isRecord(value)) return undefined
  return { x: number(value.x, 50), y: number(value.y, 50), scale: number(value.scale, 1), fit: value.fit === 'contain' ? 'contain' : 'cover' }
}

function mapStringRecord(value: JsonRecord): Record<string, string> { return Object.fromEntries(Object.entries(value).filter(([, item]) => typeof item === 'string')) as Record<string, string> }
function strings(value: unknown): string[] { return array(value).filter((item): item is string => typeof item === 'string') }
function items(value: JsonRecord): JsonRecord[] { return array(value.items).filter(isRecord) }
function record(value: unknown): JsonRecord { return isRecord(value) ? value : {} }
function array(value: unknown): unknown[] { return Array.isArray(value) ? value : [] }
function text(value: unknown): string { return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '' }
function boolean(value: unknown): boolean { return value === true }
function number(value: unknown, fallback = 0): number { const result = Number(value); return Number.isFinite(result) ? result : fallback }
function coordinateType(value: unknown): 'GCJ-02' | 'WGS84' | 'BD-09' | undefined { return value === 'GCJ-02' || value === 'WGS84' || value === 'BD-09' ? value : undefined }
function travelMode(value: unknown): 'driving' | 'walking' | 'transit' | undefined { return value === 'driving' || value === 'walking' || value === 'transit' ? value : undefined }
function telephone(value: unknown): string { return typeof value === 'string' ? value.replace(/[^\d+*-]/g, '').slice(0, 40) : '' }
function isRecord(value: unknown): value is JsonRecord { return typeof value === 'object' && value !== null && !Array.isArray(value) }
